/**
 * EmptyState — uniform "nothing here yet" surface.
 *
 * Three slots: optional icon (round halo around it), required title,
 * optional description (can carry ReactNode for links/inline buttons),
 * optional action (the call-to-action button). The action slot accepts
 * any ReactNode so the consumer chooses styling — a button, a link, a
 * row of two buttons — without us prescribing.
 *
 * PH9-8: also accepts an `illustration` ReactNode (typically an SVG)
 * that supersedes the icon halo when present. For canonical stock
 * illustrations see `./emptyIllustrations.tsx`.
 *
 * Mirrors `admin/src/components/EmptyState.tsx` so both surfaces speak
 * the same visual language. Kept as two separate files (rather than a
 * shared package) because admin and athlete diverge on padding and
 * border tokens, and a tiny duplication beats a synced abstraction.
 */
import type { ComponentType, ReactNode } from "react";


type Props = {
  icon?: ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  /** Larger illustration — overrides icon when present. ~96px box. */
  illustration?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Optional test-id pass-through so call sites can pin "this is the
   *  empty state for FeatureX". */
  testId?: string;
  className?: string;
};


export function EmptyState({
  icon: Icon,
  illustration,
  title,
  description,
  action,
  testId,
  className = "",
}: Props) {
  return (
    <div
      data-testid={testId ?? "empty-state"}
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center text-center py-10 px-4 ${className}`}
    >
      {illustration ? (
        <div
          className="mb-3 inline-flex items-center justify-center w-24 h-24 text-fg-muted"
          data-testid={`${testId ?? "empty-state"}-illustration`}
          aria-hidden="true"
        >
          {illustration}
        </div>
      ) : (
        Icon && (
          <div className="mb-3 inline-flex items-center justify-center w-12 h-12 rounded-full bg-bg-raised border border-bg-border text-fg-muted">
            <Icon size={22} strokeWidth={1.5} />
          </div>
        )
      )}
      <div className="text-sm font-semibold text-fg-primary">{title}</div>
      {description && (
        <div className="text-xs text-fg-secondary mt-1 max-w-sm leading-relaxed">
          {description}
        </div>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
